import { applyGermanize } from './germanize.js';
import { usePersistentState } from './hooks.js';
import { accent, accentBorder, border, borderLight, surface, surface2, textDim } from './tokens.js';

const { useState, useEffect } = React;

// ─── Disclosure (auffahrender Container) ─────────────────────────────────
export function Disclosure({ label, children, defaultOpen, storageKey }) {
  const [open, setOpen] = storageKey
    ? usePersistentState(storageKey, !!defaultOpen)
    : React.useState(!!defaultOpen);

  return (
    <div style={{
      border: `1px solid ${border}`, borderRadius: 8,
      background: surface, overflow: "hidden", marginBottom: 28
    }}>
      <button onClick={() => setOpen(o => !o)}
              style={{
                width: "100%", textAlign: "left",
                background: "none", border: "none", cursor: "pointer",
                padding: "14px 18px", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 10,
                color: accent
              }}>
        <span style={{
          fontSize: 12, textTransform: "uppercase", letterSpacing: 1.5,
          flex: 1, fontWeight: 700
        }}>
          {label}
        </span>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
             style={{transition: "transform 0.3s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)"}}>
          <path d="M5 3l5 5-5 5" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <div style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 0.3s ease"
      }}>
        <div style={{overflow: "hidden"}}>
          <div style={{padding: "12px 18px 18px"}}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MusterFlipCard ──────────────────────────────────────────────────────
export function MusterFlipCard({ pattern }) {
  const [flipped, setFlipped] = useState(false);
  const p = pattern;
  const toggle = () => setFlipped(f => !f);
  const faceBase = {
    position: "relative", gridArea: "1 / 1",
    borderRadius: 10, padding: "28px 24px 40px",
    transition: "opacity 0.45s ease",
    pointerEvents: "auto"
  };
  const labelStyle = {
    fontSize: 11, fontFamily: "'Raleway', system-ui, sans-serif",
    marginBottom: 14, textTransform: "uppercase", letterSpacing: 1.5
  };
  return (
    <div onClick={toggle}
         style={{display: "grid", gridTemplateColumns: "minmax(0, 1fr)", width: "100%", minHeight: 280, cursor: "pointer", marginBottom: 28}}
         role="button" aria-pressed={flipped} tabIndex={0}
         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}>
      {/* Beispiel */}
      <div style={{
        ...faceBase,
        background: surface, border: `1px solid ${border}`,
        fontFamily: "Georgia, serif", fontSize: 17, lineHeight: 1.65,
        color: "#e8e6e3",
        opacity: flipped ? 0 : 1, pointerEvents: flipped ? "none" : "auto"
      }} aria-hidden={flipped}>
        <div style={{...labelStyle, color: textDim}}>
          {applyGermanize("Beispiel")}
        </div>
        <div>{applyGermanize(p.example)}</div>
        <div style={{position: "absolute", bottom: 12, right: 16, color: textDim, fontSize: 11}}>
          {applyGermanize("tippen zum Aufdecken")}
        </div>
      </div>
      {/* Erkennungsmerkmale */}
      <div style={{
        ...faceBase,
        background: surface2, border: `1px solid ${accentBorder}`,
        color: "#e8ede9",
        opacity: flipped ? 1 : 0, pointerEvents: flipped ? "auto" : "none"
      }} aria-hidden={!flipped}>
        <div style={{...labelStyle, color: accent}}>
          {applyGermanize("Erkennungsmerkmale")}
        </div>
        <ul style={{paddingLeft: 20, lineHeight: 1.6, fontFamily: "Georgia, serif", fontSize: 15}}>
          {p.detection.map((d, i) => <li key={i} style={{marginBottom: 10}}>{applyGermanize(d)}</li>)}
        </ul>
      </div>
    </div>
  );
}

// ─── MilestoneToast ──────────────────────────────────────────────────────
export function MilestoneToast({ text, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div role="status" style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      zIndex: 400, background: surface2, border: `1px solid ${borderLight}`,
      borderRadius: 12, padding: "12px 18px", display: "flex", alignItems: "center",
      gap: 14, boxShadow: "0 4px 28px rgba(0,0,0,0.45)", maxWidth: "calc(100vw - 36px)",
      animation: "obscuraFadeIn 0.3s ease"
    }}>
      <span style={{fontSize: 14, color: "#e8e6e3", fontFamily: "Georgia, serif", fontStyle: "italic", lineHeight: 1.5}}>
        {applyGermanize(text)}
      </span>
      <button onClick={onClose} aria-label="Schliessen" style={{
        background: "none", border: "none", cursor: "pointer", color: textDim,
        fontSize: 15, padding: 2, lineHeight: 1, flexShrink: 0
      }}>✕</button>
    </div>
  );
}
